import ic_chat from '@/assets/icons/modules/ic_mod_chat.svg?raw';
import ic_classroom from '@/assets/icons/modules/ic_mod_classroom.svg?raw';
import ic_proximity from '@/assets/icons/modules/ic_mod_kidiradar.svg?raw';
import ic_report from '@/assets/icons/modules/ic_mod_report.svg?raw';
import { Selection } from "d3";
import "../../../styles/Base-ui.scss";
import { GroupChangeEvent, groupContext } from "../../context/group";
import { _APP_DATA } from "../../data/AppData";
import { EEventId } from "../../data/models/Entities";
import { _SvAlumnoObtenerLista } from "../../data/services/Alumno";
import { _CHAT_GRUPOS_MENSAJES_NOLEIDOS_MAP, _LoadUnreadedMessage } from "../../data/services/Chat";
import { _ESCOLARIDAD_CONFIGURACION_MAP, _SvEscolaridadObtenerConfiguracion } from "../../data/services/Escolaridad";
import {
    _DICT_ESCUELAS,
    _SvEscuelaObtenerConfiguracion,
    _SvEscuelaSubscribeChanges,
} from '../../data/services/Escuela';
import { _HistoryBackEvalAndTry } from "../../routes/Router";
import { _HashAssign } from "../../routes/UIManager";
import { ActionMenuItem } from "../components-lit/ActionMenu/ActionMenu";
import '../components-lit/TopBar/TopBar';
import { TopBar } from "../components-lit/TopBar/TopBar";
import { CreateLogoOptions } from "../components/LogoOptions";
import { _PROXIMIDAD_CTRL } from "../controllers/ProximidadController";
import { Base } from "./Base";

interface IBaseConfig {
    addMainLogoOptions: boolean;
    addOptionsInHeader: boolean;
    monitorChat: boolean;
    addFooter?: boolean;
    className?: string;
    includeGroupInHeaderTitle?: boolean;
    requiresGroupChild?: boolean;
    timmerLoadUnreadedChat?: number;
}

export interface BaseUIActionMenuItem extends ActionMenuItem {
    Order: number;
}

export const MAIN_TOP_OPTIONS_CATALOG = {
    "proximity": {
        ID: "opt_proximity", Color: "var(--color-proximity)", Icon: ic_proximity,
        Label: "Proximidad", Order: 8,
        onClick: (e: Event) => _PROXIMIDAD_CTRL._ToggleModal(e.currentTarget as HTMLElement),
    } as BaseUIActionMenuItem,
    "classroom": {
        ID: "opt_classroom", Color: "var(--color-win)", Icon: ic_classroom,
        Label: "Salón", Order: 3,
        onClick: () => _HashAssign("#classroom"),
    } as BaseUIActionMenuItem,
    "report": {
        ID: "opt_report", Color: "var(--color-orange)", Icon: ic_report,
        Label: "Reporte", Order: 2,
        onClick: () => _HashAssign("#report"),
    } as BaseUIActionMenuItem,
    "chat": {
        ID: "opt_chat", Color: "var(--color-hygiene)", Icon: ic_chat,
        Label: "Chat", Order: 1,
        onClick: () => _HashAssign("#chat"),
    } as BaseUIActionMenuItem,
} as const;

export class BaseUI extends Base {
    protected headerContainer: Selection<HTMLDivElement, any, any, any>;
    protected bodyContainer: Selection<HTMLDivElement, any, any, any>;
    protected footerContainer: Selection<HTMLDivElement, any, any, any>;

    protected timmerLoadUnreadedChat: number = 1000 * 30;
    protected baseConfig: IBaseConfig;
    protected hasChatConfig: boolean = false;

    private idTimmerLoadUnreaded: NodeJS.Timeout;
    private requiresGroupChildren: boolean = false;
    private unsubscribeEscuelaChanges: (() => void) | null = null;

    protected topBarElement: TopBar;
    // protected currentHeaderOptions: BaseUIActionMenuItem[] = [];

    /** Opciones activas de la barra de encabezado. Por ejemplo: Logout, Ir a salón, etc... */
    protected activeHeaderOptions: Map<string, BaseUIActionMenuItem> = new Map();

    constructor(config?: IBaseConfig) {
        config = config || { addMainLogoOptions: true, addOptionsInHeader: true, monitorChat: true, timmerLoadUnreadedChat: 1000 * 30 };
        const cls = "main-container " + (config.className || "");
        super(cls, true);
        this.baseConfig = config;
        this.baseConfig.addFooter = config.addFooter || false;
        this.baseConfig.includeGroupInHeaderTitle = config.includeGroupInHeaderTitle == null ? true : config.includeGroupInHeaderTitle;
        this.baseConfig.timmerLoadUnreadedChat = config.timmerLoadUnreadedChat == null ? 1000 * 30 : config.timmerLoadUnreadedChat;
        this.requiresGroupChildren = config.requiresGroupChild;
        this.headerContainer = this.mainContainer.append("div").classed("header", true);
        this.CreateTopBar(this.baseConfig.addOptionsInHeader);
        if (this.baseConfig.addMainLogoOptions)
            CreateLogoOptions(this.mainContainer);

        this.bodyContainer = this.mainContainer.append("div").classed("body-container", true);
        if (!this.baseConfig.addMainLogoOptions)
            this.bodyContainer.classed("empty", true);

        this.footerContainer = this.mainContainer.append("div")
        if (this.baseConfig.addFooter) {
            this.footerContainer.classed("footer-container", true);
            this.bodyContainer.classed("add-footer", true);
        }
        this.InitBase();
    }

    private async InitBase() {
        const viewValid = await this.ValidateViewPermission();
        if (!viewValid) {
            _HistoryBackEvalAndTry("default");
            return;
        }

        if (this.baseConfig.monitorChat) {
            this.ValidateChat();
            this.LoadConfigAndunreadedMsg();
        }

        const onchangegroup: ((e: GroupChangeEvent) => void) = this.OnChangeGroup.bind(this);
        this["__onchangegroup"] = onchangegroup;
        groupContext.addOnGroupChangeEventListener(onchangegroup)

        if (this.requiresGroupChildren) {
            await this.RefreshAlumnosMap();
        }

        this.InitClass();
    }

    protected ValidateViewPermission(): boolean | Promise<boolean> {
        return true;
    }

    protected InitClass(): void | Promise<any> {

    }

    protected SyncTopBarOptions() {
        if (!this.topBarElement) return;
        const optionsArray = Array.from(this.activeHeaderOptions.values())
            .sort((a, b) => a.Order - b.Order).reverse();
        this.topBarElement.k_options = optionsArray;
    }

    protected async OnChangeGroup(_: GroupChangeEvent): Promise<void> {
        this.UpdateTopBarTitle();
        if (this.baseConfig.monitorChat) {
            this.ValidateChat();
            this.LoadConfigAndunreadedMsg();
        }

        if (this.requiresGroupChildren) {
            await this.RefreshAlumnosMap();
        }
    }

    protected RefreshAlumnosMap() {
        return new Promise<void>((resolve) => {
            _SvAlumnoObtenerLista(() => {
                resolve();
                _PROXIMIDAD_CTRL._RefreshUI();
            })
        })
    }

    protected LoadConfigAndunreadedMsg() {
        if (this.idTimmerLoadUnreaded) {
            clearInterval(this.idTimmerLoadUnreaded);
            this.idTimmerLoadUnreaded = null;
        }

        if (_APP_DATA.userData.IdNivel === 0) return;

        const hasChatConfigInit = this.hasChatConfig;
        if (this.hasChatConfig)
            _LoadUnreadedMessage((_) => this.OnChangeOnreadedMessage(), null, true);

        _SvEscolaridadObtenerConfiguracion(_APP_DATA.userData.IdNivel, (_, result) => {
            if (result.length > 0) {
                this.ValidateChat();
                if (!hasChatConfigInit && this.hasChatConfig)
                    _LoadUnreadedMessage((_) => this.OnChangeOnreadedMessage(), null, true);
            }
        });

        this.idTimmerLoadUnreaded = setTimeout(() => {
            this.LoadConfigAndunreadedMsg();
        }, this.timmerLoadUnreadedChat);
    }

    private ValidateChat() {
        let itemChat = Array.from(_ESCOLARIDAD_CONFIGURACION_MAP.values()).filter(o => o.Tipo == EEventId.CHAT);
        this.hasChatConfig = itemChat.length > 0;

        this.OnValidateChatV2();
    }

    /**
     * Actualiza el título y subtítulo de la barra superior según la configuración.
     * Si includeGroupInHeaderTitle está habilitado, muestra el nombre del grupo como título y el nombre del maestro como subtítulo.
     * De lo contrario, muestra solo el nombre del maestro como título.
     */
    protected UpdateTopBarTitle() {
        let title = this.baseConfig.includeGroupInHeaderTitle ? _APP_DATA.userData.NombreGrupo : "";
        const teacherName = _APP_DATA.userData.NombreMaestra + " " + _APP_DATA.userData.ApPaternoMaestra + " " + _APP_DATA.userData.ApMaternoMaestra;
        if (this.baseConfig.includeGroupInHeaderTitle) {
            this.topBarElement.k_title = title;
            this.topBarElement.k_subtitle = teacherName;
        } else {
            this.topBarElement.k_title = teacherName;
        }
    }

    protected OnValidateChat() {
        if (this.baseConfig.addOptionsInHeader) {
            const btnChat = this.headerContainer.select(".div-options").select(".chat-icon-container");
            btnChat.classed("hide", !this.hasChatConfig);
        }
    }

    protected OnValidateChatV2() {
        if (!this.baseConfig.addOptionsInHeader || !this.topBarElement) return;
        if (!this.hasChatConfig) {
            this.activeHeaderOptions.delete(MAIN_TOP_OPTIONS_CATALOG.chat.ID);
            return;
        }

        this.activeHeaderOptions.set(MAIN_TOP_OPTIONS_CATALOG.chat.ID, MAIN_TOP_OPTIONS_CATALOG["chat"]);
        this.SyncTopBarOptions();
    }

    protected CreateTopBar(addOptions: boolean) {
        this.topBarElement = this.headerContainer.append<TopBar>('app-top-bar').node();
        this.topBarElement.k_showLeadingContent = false;
        // this.topBarElement.style.borderBottom = '1px solid var(--md-sys-color-outline-variant, #CAC4D0)'
        this.UpdateTopBarTitle();

        this.unsubscribeEscuelaChanges = _SvEscuelaSubscribeChanges(() => this.OnLoadDataProximidad());
        _PROXIMIDAD_CTRL.onLoadDataCallback = () => this.OnLoadDataProximidad();

        if (_APP_DATA.userData?.IdKinder) {
            _SvEscuelaObtenerConfiguracion(() => this.OnLoadDataProximidad());
        }

        if (!addOptions) return;

        this.activeHeaderOptions.set(MAIN_TOP_OPTIONS_CATALOG.classroom.ID, MAIN_TOP_OPTIONS_CATALOG.classroom);
        this.activeHeaderOptions.set(MAIN_TOP_OPTIONS_CATALOG.report.ID, MAIN_TOP_OPTIONS_CATALOG.report);
        this.activeHeaderOptions.set(MAIN_TOP_OPTIONS_CATALOG.chat.ID, MAIN_TOP_OPTIONS_CATALOG.chat);

        if (this.HasAnySchoolKidiRadarActivo()) {
            this.activeHeaderOptions.set(MAIN_TOP_OPTIONS_CATALOG.proximity.ID, MAIN_TOP_OPTIONS_CATALOG.proximity);
        }

        this.OnLoadDataProximidad();
        this.SyncTopBarOptions();
        requestAnimationFrame(() => this.OnLoadDataProximidad());
    }

    protected HasAnySchoolKidiRadarActivo(): boolean {
        return Array.from(_DICT_ESCUELAS.values()).some(escuela => !!escuela && escuela.getKidiRadarActivo());
    }

    protected OnLoadDataProximidad() {
        const proximityId = MAIN_TOP_OPTIONS_CATALOG.proximity.ID;
        const someSchoolHasRadar = this.HasAnySchoolKidiRadarActivo();

        if (!someSchoolHasRadar) {
            this.activeHeaderOptions.delete(proximityId);
            this.SyncTopBarOptions();
            requestAnimationFrame(() => this.SyncTopBarOptions());
            return;
        }

        const countMsg = _PROXIMIDAD_CTRL.requireBadge ? 1 : 0;
        this.activeHeaderOptions.set(proximityId, {
            ...MAIN_TOP_OPTIONS_CATALOG.proximity,
            Badge: countMsg > 0 ? "ㅤ" : undefined,
        });

        this.SyncTopBarOptions();
        requestAnimationFrame(() => this.SyncTopBarOptions());
    }

    protected OnChangeOnreadedMessage() {
        const countMsg = _CHAT_GRUPOS_MENSAJES_NOLEIDOS_MAP.get(_APP_DATA.userData.IdGrupo)?.countMsg || 0;
        this.activeHeaderOptions.set(MAIN_TOP_OPTIONS_CATALOG.chat.ID, { ...MAIN_TOP_OPTIONS_CATALOG.chat, Badge: countMsg > 0 ? countMsg : undefined })
        this.SyncTopBarOptions();
        // this.headerContainer.select(".chat-icon-container").select(".num-messages")
        //     .classed("active", countMsg > 0)
        //     .text(countMsg);
    }

    public OnDestroy(): void {
        if (this["__onchangegroup"]) {
            groupContext.removeOnGroupChangeEventListener(this["__onchangegroup"]);
        }

        if (this.unsubscribeEscuelaChanges) {
            this.unsubscribeEscuelaChanges();
            this.unsubscribeEscuelaChanges = null;
        }

        _PROXIMIDAD_CTRL.onLoadDataCallback = null;

        clearInterval(this.idTimmerLoadUnreaded);
        this.idTimmerLoadUnreaded = null;

        super.OnDestroy();
    }
}